"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import makeStyles from "../../theming/make-styles.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
const useStyles = makeStyles()((theme, { checked, color, disabled, focused, reverseOrder, round }) => {
    const palette = theme.palette(color).switch;
    return {
        icon: {
            width: theme.size(10),
            height: theme.size(10),
            color: palette.icon.color,
            stroke: palette.icon.strokeColor,
        },
        iconWrapper: Object.assign(Object.assign({ "display": "inline-flex", "flexShrink": 0, "alignItems": "center", "justifyContent": "center", "width": theme.size(16), "height": theme.size(16), "border": `${theme.size(1)} solid`, "borderRadius": round ? "100%" : theme.size(2) }, (disabled
            ? {
                backgroundColor: (checked ? palette.disabled.checked : palette.disabled).backgroundColor,
                borderColor: (checked ? palette.disabled.checked : palette.disabled).borderColor,
            }
            : {
                backgroundColor: (checked ? palette.checked : palette).backgroundColor,
                borderColor: (checked ? palette.checked : focused ? palette.focus : palette).borderColor,
                boxShadow: focused ? theme.outline(palette.focus.boxShadowColor) : undefined,
            })), { "&:hover, label:hover &": !disabled && checked
                ? {
                    backgroundColor: palette.checked.hover.backgroundColor,
                    borderColor: palette.checked.hover.borderColor,
                }
                : !disabled && {
                    backgroundColor: palette.hover.backgroundColor,
                    borderColor: (focused ? palette.focus : palette.hover).borderColor,
                }, "&:active": !disabled &&
                checked && {
                backgroundColor: palette.checked.active.backgroundColor,
                borderColor: palette.checked.active.borderColor,
            } }),
        input: {
            position: "absolute",
            opacity: 0,
            appearance: "none",
        },
        label: Object.assign(Object.assign({}, theme.typography.small), { marginRight: reverseOrder ? theme.spacing("sm") : undefined, marginLeft: reverseOrder ? undefined : theme.spacing("sm") }),
        toggle: {
            display: "inline-flex",
        },
        wrapper: {
            display: "inline-flex",
            flexDirection: reverseOrder ? "row-reverse" : undefined,
            justifyContent: reverseOrder ? "flex-end" : "flex-start",
            alignItems: "center",
            width: "max-content",
            fontSize: theme.typography.body.fontSize,
            color: disabled ? palette.disabled.color : undefined,
            cursor: disabled ? "not-allowed" : "pointer",
            userSelect: "none",
        },
    };
});
export const SwitchBase = React.forwardRef(function SwitchBase(_a, ref) {
    var { checked, children, className, color = "primary", disabled, icon: Icon, indeterminate = false, preventDefault = false, readOnly, reverseOrder = false, round = false, stopPropagation = true, toggle, type, onBlur, onChange, onFocus } = _a, props = __rest(_a, ["checked", "children", "className", "color", "disabled", "icon", "indeterminate", "preventDefault", "readOnly", "reverseOrder", "round", "stopPropagation", "toggle", "type", "onBlur", "onChange", "onFocus"]);
    const [focused, setFocused] = useState(false);
    const { classes, cx } = useStyles({ checked: indeterminate || !!checked, color, disabled, focused, reverseOrder, round });
    const innerRef = useRef(ref);
    const combinedRef = useCombinedRefs(ref, innerRef);
    useEffect(() => {
        if (innerRef.current) {
            innerRef.current.indeterminate = indeterminate;
        }
    }, [indeterminate, ref]);
    const handleBlur = useCallback((e) => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        setFocused(false);
    }, [onBlur]);
    const handleChange = useCallback((e) => {
        if (!readOnly) {
            onChange === null || onChange === void 0 ? void 0 : onChange(e.target.checked, e);
        }
    }, [onChange, readOnly]);
    const handleFocus = useCallback((e) => {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        setFocused(true);
    }, [onFocus]);
    return (_jsxs("label", { ref: ref, className: cx(classes.wrapper, className), onClick: e => {
            preventDefault && e.preventDefault();
            stopPropagation && e.stopPropagation();
        }, children: [_jsx("input", Object.assign({ ref: combinedRef, "aria-checked": indeterminate ? "mixed" : !!checked, "aria-disabled": disabled, checked: checked, className: classes.input, disabled: disabled, readOnly: readOnly, type: type, onBlur: handleBlur, onChange: handleChange, onFocus: handleFocus }, props)), Icon && (_jsx("span", { className: classes.iconWrapper, children: _jsx(Icon, { checked: !!checked, className: classes.icon, indeterminate: indeterminate }) })), toggle && _jsx("span", { className: classes.toggle, children: toggle }), children && _jsx("span", { className: classes.label, children: children })] }));
});
export default SwitchBase;
//# sourceMappingURL=switch-base.js.map